import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Mail, Users, Settings as SettingsIcon, Send, RefreshCw, Loader2,
  CheckCircle, XCircle, Clock, AlertCircle, PlayCircle, Database, Download, HardDrive,
  Upload, Lock, Eye, EyeOff, AlertTriangle, FileArchive, RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  SENT: { variant: 'default', icon: CheckCircle, className: 'bg-green-100 text-green-700 hover:bg-green-100' },
  FAILED: { variant: 'destructive', icon: XCircle, className: '' },
  PENDING: { variant: 'secondary', icon: Clock, className: '' },
};

export default function AdminSettings() {
  const navigate = useNavigate();
  const [token] = useState(() => sessionStorage.getItem('harmony_token'));

  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ apps: [], events: {} });
  const [settings, setSettings] = useState({ global: {}, apps: {}, events: {} });
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  // Per-app local edit state for daily_summary fields (text inputs)
  const [appEdits, setAppEdits] = useState({});

  const apiClient = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, set, lg] = await Promise.all([
        apiClient.get('/notifications/event-catalog'),
        apiClient.get('/notifications/settings'),
        apiClient.get('/notifications/log?limit=50'),
      ]);
      setCatalog(cat.data);
      setSettings(set.data);
      setLog(lg.data);
      // seed appEdits with current values
      const edits = {};
      Object.entries(set.data.apps || {}).forEach(([app, s]) => {
        edits[app] = {
          notification_recipients: (s.notification_recipients || []).join(', '),
          daily_summary_time: s.daily_summary_time || '09:00',
          daily_summary_recipients: (s.daily_summary_recipients || []).join(', '),
        };
      });
      setAppEdits(edits);
    } catch (e) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadLog = async () => {
    setLogLoading(true);
    try {
      const lg = await apiClient.get('/notifications/log?limit=50');
      setLog(lg.data);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ----- Save helpers -----
  const setGlobalToggle = async (value) => {
    setSavingKey('global');
    try {
      await apiClient.put('/notifications/settings/global', { value });
      setSettings((s) => ({ ...s, global: { ...s.global, email_enabled: value } }));
      toast.success(`Global email notifications ${value ? 'ENABLED' : 'DISABLED'}`);
    } catch (e) {
      toast.error('Failed to update global setting');
    } finally {
      setSavingKey(null);
    }
  };

  const setAppToggle = async (app, key, value) => {
    setSavingKey(`${app}.${key}`);
    try {
      await apiClient.put(`/notifications/settings/app/${app}`, { [key]: value });
      setSettings((s) => ({
        ...s,
        apps: { ...s.apps, [app]: { ...s.apps[app], [key]: value } },
      }));
      toast.success('Updated');
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSavingKey(null);
    }
  };

  const setEventToggle = async (app, eventType, value) => {
    setSavingKey(`${app}.${eventType}`);
    try {
      await apiClient.put(`/notifications/settings/event/${app}/${eventType}`, { value });
      setSettings((s) => ({
        ...s,
        events: {
          ...s.events,
          [app]: { ...s.events[app], [eventType]: value },
        },
      }));
    } catch (e) {
      toast.error('Failed to update event toggle');
    } finally {
      setSavingKey(null);
    }
  };

  const saveAdminRecipients = async (app) => {
    const edits = appEdits[app] || {};
    const recipientsArr = (edits.notification_recipients || '')
      .split(',').map(s => s.trim()).filter(Boolean);
    setSavingKey(`${app}.notification_recipients`);
    try {
      await apiClient.put(`/notifications/settings/app/${app}`, {
        notification_recipients: recipientsArr,
      });
      setSettings((s) => ({
        ...s,
        apps: { ...s.apps, [app]: { ...s.apps[app], notification_recipients: recipientsArr } },
      }));
      toast.success(recipientsArr.length === 0
        ? 'Admin recipients cleared — admin-style events will not send for this app'
        : `Saved ${recipientsArr.length} admin recipient(s)`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Save failed';
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  const saveDailySummaryFields = async (app) => {
    const edits = appEdits[app] || {};
    const recipientsArr = (edits.daily_summary_recipients || '')
      .split(',').map(s => s.trim()).filter(Boolean);
    setSavingKey(`${app}.summaryfields`);
    try {
      await apiClient.put(`/notifications/settings/app/${app}`, {
        daily_summary_time: edits.daily_summary_time || '09:00',
        daily_summary_recipients: recipientsArr,
      });
      setSettings((s) => ({
        ...s,
        apps: {
          ...s.apps,
          [app]: {
            ...s.apps[app],
            daily_summary_time: edits.daily_summary_time,
            daily_summary_recipients: recipientsArr,
          },
        },
      }));
      toast.success('Daily summary settings saved');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Save failed';
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  const triggerDailySummaryNow = async (app) => {
    setSavingKey(`${app}.send-summary`);
    try {
      const res = await apiClient.post(`/notifications/daily-summary/${app}`, {});
      toast.success(`Daily summary sent (resend_id: ${res.data.resend_id || 'ok'})`);
      reloadLog();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to send daily summary';
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Recipient required');
      return;
    }
    setTestSending(true);
    try {
      const res = await apiClient.post('/notifications/test', { recipient: testEmail });
      toast.success(`Test sent (resend_id: ${res.data.resend_id})`);
      setTestDialogOpen(false);
      setTestEmail('');
      reloadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const globalOn = !!settings.global.email_enabled;
  const appsArr = catalog.apps || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-settings-page">
        {/* Header + sub-nav */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              System Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage global system configuration and email notifications
            </p>
          </div>
          <Button variant="outline" onClick={() => setTestDialogOpen(true)} data-testid="admin-settings-test-email-btn">
            <Send className="mr-2 h-4 w-4" /> Send Test Email
          </Button>
        </div>

        {/* Admin Sub-Nav */}
        <div className="flex gap-2 border-b">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} className="rounded-none border-b-2 border-transparent hover:border-muted">
            <Users className="w-4 h-4 mr-2" /> Users
          </Button>
          <Button variant="ghost" size="sm" className="rounded-none border-b-2 border-primary text-primary">
            <SettingsIcon className="w-4 h-4 mr-2" /> System Settings
          </Button>
        </div>

        <Tabs defaultValue="email-notifications" className="space-y-4">
          <TabsList>
            <TabsTrigger value="email-notifications" data-testid="admin-settings-tab-email">
              <Mail className="w-4 h-4 mr-2" /> Email Notifications
            </TabsTrigger>
            <TabsTrigger value="log" data-testid="admin-settings-tab-log">
              <Clock className="w-4 h-4 mr-2" /> Recent Email Log
            </TabsTrigger>
            <TabsTrigger value="backup" data-testid="admin-settings-tab-backup">
              <Database className="w-4 h-4 mr-2" /> Backup
            </TabsTrigger>
            <TabsTrigger value="restore" data-testid="admin-settings-tab-restore">
              <Upload className="w-4 h-4 mr-2" /> Restore
            </TabsTrigger>
          </TabsList>

          {/* Email Notifications Tab */}
          <TabsContent value="email-notifications" className="space-y-6">
            {/* Master switch */}
            <Card className={!globalOn ? 'border-amber-300 bg-amber-50/30' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Master Email Switch
                    </CardTitle>
                    <CardDescription>
                      Disable to halt <b>all</b> outgoing emails across all apps (event + daily summaries).
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={globalOn ? 'default' : 'outline'}>{globalOn ? 'ENABLED' : 'DISABLED'}</Badge>
                    <Switch
                      checked={globalOn}
                      onCheckedChange={(v) => setGlobalToggle(v)}
                      disabled={savingKey === 'global'}
                      data-testid="admin-settings-global-toggle"
                    />
                  </div>
                </div>
              </CardHeader>
              {!globalOn && (
                <CardContent className="pt-0">
                  <div className="text-sm text-amber-800 bg-amber-100 border border-amber-200 rounded p-3">
                    ⚠️ Master switch is OFF. Even if per-app or per-event toggles are ON below, no email will be delivered.
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Per-app sections */}
            {appsArr.map((appMeta) => {
              const app = appMeta.key;
              const appCfg = settings.apps[app] || {};
              const events = catalog.events?.[app] || [];
              const eventsState = settings.events?.[app] || {};
              const appOn = !!appCfg.email_enabled;
              const summaryOn = !!appCfg.daily_summary_enabled;
              const edits = appEdits[app] || {};
              return (
                <Card key={app} data-testid={`admin-settings-app-${app}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <CardTitle>{appMeta.label}</CardTitle>
                        <CardDescription>App-level master toggle, daily summary, and event toggles</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={appOn ? 'default' : 'outline'}>{appOn ? 'ENABLED' : 'DISABLED'}</Badge>
                        <Switch
                          checked={appOn}
                          onCheckedChange={(v) => setAppToggle(app, 'email_enabled', v)}
                          disabled={savingKey === `${app}.email_enabled`}
                          data-testid={`admin-settings-app-toggle-${app}`}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Admin Recipients block */}
                    <div className="rounded-lg border bg-blue-50/40 p-4 space-y-3" data-testid={`admin-settings-app-recipients-block-${app}`}>
                      <div>
                        <p className="font-semibold text-sm flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Admin Recipients
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Static recipients for admin-style events (e.g., asset created, expense submitted alerts). Comma-separated.
                          <br />
                          Dynamic recipients (allocated employee, candidate, uploader, etc.) are automatically added by each event.
                          {((appCfg.notification_recipients || []).length === 0) && (
                            <span className="block mt-1 text-amber-700 font-medium">
                              ⚠️ Empty — admin-style events will not be sent for this app until you add at least one recipient.
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Recipients (comma-separated emails)</Label>
                          <Input
                            type="text"
                            value={edits.notification_recipients || ''}
                            onChange={(e) => setAppEdits((s) => ({ ...s, [app]: { ...s[app], notification_recipients: e.target.value } }))}
                            placeholder="admin@harmonydi.com, team-lead@harmonydi.com"
                            data-testid={`admin-settings-app-recipients-${app}`}
                          />
                          {(appCfg.notification_recipients || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1">
                              {appCfg.notification_recipients.map((email, idx) => (
                                <Badge key={idx} variant="secondary" className="text-[10px]">{email}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveAdminRecipients(app)}
                          disabled={savingKey === `${app}.notification_recipients`}
                          data-testid={`admin-settings-app-recipients-save-${app}`}
                        >
                          {savingKey === `${app}.notification_recipients` && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                          Save Recipients
                        </Button>
                      </div>
                    </div>

                    {/* Daily Summary block */}
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-semibold text-sm flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Daily Summary
                          </p>
                          <p className="text-xs text-muted-foreground">Aggregated email recapping the last 24h, sent once per day.</p>
                        </div>
                        <Switch
                          checked={summaryOn}
                          onCheckedChange={(v) => setAppToggle(app, 'daily_summary_enabled', v)}
                          disabled={savingKey === `${app}.daily_summary_enabled`}
                          data-testid={`admin-settings-app-summary-toggle-${app}`}
                        />
                      </div>
                      <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${summaryOn ? '' : 'opacity-60 pointer-events-none'}`}>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Time (UTC, HH:MM)</Label>
                          <Input
                            type="text"
                            value={edits.daily_summary_time || ''}
                            onChange={(e) => setAppEdits((s) => ({ ...s, [app]: { ...s[app], daily_summary_time: e.target.value } }))}
                            placeholder="09:00"
                            data-testid={`admin-settings-app-summary-time-${app}`}
                          />
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                          <Label className="text-xs">Recipients (comma-separated)</Label>
                          <Input
                            type="text"
                            value={edits.daily_summary_recipients || ''}
                            onChange={(e) => setAppEdits((s) => ({ ...s, [app]: { ...s[app], daily_summary_recipients: e.target.value } }))}
                            placeholder="alice@harmonydi.com, bob@harmonydi.com"
                            data-testid={`admin-settings-app-summary-recipients-${app}`}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveDailySummaryFields(app)}
                          disabled={savingKey === `${app}.summaryfields`}
                          data-testid={`admin-settings-app-summary-save-${app}`}
                        >
                          {savingKey === `${app}.summaryfields` && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                          Save Schedule
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => triggerDailySummaryNow(app)}
                          disabled={savingKey === `${app}.send-summary`}
                          data-testid={`admin-settings-app-summary-sendnow-${app}`}
                        >
                          {savingKey === `${app}.send-summary` ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <PlayCircle className="mr-2 h-3 w-3" />}
                          Send Now (test)
                        </Button>
                      </div>
                    </div>

                    {/* Event toggles */}
                    <div className={appOn ? '' : 'opacity-50'}>
                      <p className="font-semibold text-sm mb-2">Event Notifications ({events.length})</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {events.map((ev) => {
                          const on = !!eventsState[ev.event_type];
                          const key = `${app}.${ev.event_type}`;
                          return (
                            <div
                              key={ev.event_type}
                              className="flex items-start justify-between gap-3 rounded border p-3"
                              data-testid={`admin-settings-event-${app}-${ev.event_type}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium">{ev.label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{ev.event_type}</code>
                                  {ev.recipients_hint && <span className="ml-2">→ {ev.recipients_hint}</span>}
                                </p>
                              </div>
                              <Switch
                                checked={on}
                                onCheckedChange={(v) => setEventToggle(app, ev.event_type, v)}
                                disabled={savingKey === key || !appOn}
                                data-testid={`admin-settings-event-toggle-${app}-${ev.event_type}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Log Tab */}
          <TabsContent value="log">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Email Activity</CardTitle>
                    <CardDescription>Last 50 attempted / successful / failed sends</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={reloadLog} disabled={logLoading}>
                    {logLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {log.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No emails sent yet.
                  </div>
                ) : (
                  <ScrollArea className="max-h-[600px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When (UTC)</TableHead>
                          <TableHead>App</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead>Recipients</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Detail</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {log.map((l) => {
                          const cfg = STATUS_BADGE[l.status] || STATUS_BADGE.PENDING;
                          const Icon = cfg.icon;
                          return (
                            <TableRow key={l.id}>
                              <TableCell className="text-xs whitespace-nowrap">
                                {new Date(l.timestamp).toLocaleString('en-GB', { timeZone: 'UTC' })}
                              </TableCell>
                              <TableCell className="text-xs">{l.app}</TableCell>
                              <TableCell className="font-mono text-[11px]">{l.event_type}</TableCell>
                              <TableCell className="text-xs">
                                {(l.recipients || []).slice(0, 2).join(', ')}
                                {(l.recipients || []).length > 2 && <span className="text-muted-foreground">{' +'}{l.recipients.length - 2}</span>}
                              </TableCell>
                              <TableCell>
                                <Badge variant={cfg.variant} className={cfg.className}>
                                  <Icon className="w-3 h-3 mr-1" />{l.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={l.error || l.subject || ''}>
                                {l.error || l.subject}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup Tab */}
          <TabsContent value="backup" className="space-y-4">
            <BackupCard token={token} />
          </TabsContent>

          {/* Restore Tab */}
          <TabsContent value="restore" className="space-y-4">
            <RestoreCard token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Verify Resend + DNS wiring. This bypasses all toggle layers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Recipient</Label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@harmonydi.com"
              data-testid="admin-settings-test-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>Cancel</Button>
            <Button onClick={sendTestEmail} disabled={testSending} data-testid="admin-settings-test-email-send-btn">
              {testSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Backup tab: preview + one-click download
// ─────────────────────────────────────────────────────────────
// User-friendly labels for the app scope dropdown. Keys must match the
// slugs returned by /api/admin/backup/apps; unknown slugs fall through
// to a title-cased default so a new backend app slug shows up here
// automatically without a UI redeploy.
const APP_LABELS = {
  all: 'All apps (full DB)',
  timesheet: 'Harmony Timesheet',
  recruitment: 'Recruitment Portal',
  expense: 'Expense Manager',
  asset: 'Asset Manager',
  form_builder: 'Form Builder',
  projects: 'Harmony Projects',
  phishpass: 'PhishPass',
};

const _appLabel = (slug) => APP_LABELS[slug] || slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function BackupCard({ token }) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [includeGridFS, setIncludeGridFS] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Per-app scope. 'all' = the historical full-DB backup; any other value
  // narrows /preview + /download to that app's collections + GridFS
  // buckets. See APP_COLLECTIONS in backend/admin_backup_routes.py for the
  // authoritative membership rules.
  const [appScope, setAppScope] = useState('all');
  const [appList, setAppList] = useState(['all']);

  // Load the app list once — the backend tells us which app slugs are
  // valid so we don't have to hard-code them here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/backup/apps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const apps = ['all', ...(r.data?.apps || [])];
          setAppList(apps);
        }
      } catch (_) { /* keep the ['all'] fallback */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Preview refetches whenever the app scope changes — the numbers on
  // the header cards (Collections, Documents, GridFS files/size) need
  // to reflect the current selection so the admin knows what they're
  // about to download.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const params = appScope && appScope !== 'all' ? { app: appScope } : {};
        const r = await axios.get(`${API}/admin/backup/preview`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        toast.error('Failed to load backup preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, appScope]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = { include_gridfs: includeGridFS };
      if (password) params.password = password;
      if (appScope && appScope !== 'all') params.app = appScope;
      const r = await axios.get(`${API}/admin/backup/download`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000,
      });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `harmony-backup-${Date.now()}.zip`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
      toast.success(`Downloaded ${filename} (${sizeMb} MB)`);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Super Admin only' : 'Backup download failed');
    } finally {
      setDownloading(false);
    }
  };

  const totalGridFsMb = (preview?.gridfs_buckets || []).reduce((s, b) => s + (b.total_size_mb || 0), 0);

  return (
    <Card data-testid="backup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Database Backup
        </CardTitle>
        <CardDescription>
          One-click snapshot of a single app's data OR the whole database as a downloadable ZIP. Super Admin only.
          The ZIP contains <code>collections/&lt;name&gt;.json</code> for each collection plus a <code>manifest.json</code> with timestamps and counts.
          <br />
          <span className="text-xs text-slate-500">
            Per-app backups exclude shared reference data (users, employees, notification settings) — a restore assumes the destination DB already has those. Choose <b>All apps</b> for a self-contained snapshot.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="backup-app-scope" className="font-medium">Scope</Label>
          <select
            id="backup-app-scope"
            value={appScope}
            onChange={(e) => setAppScope(e.target.value)}
            className="mt-1 block w-full md:w-72 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            data-testid="backup-app-scope"
          >
            {appList.map((slug) => (
              <option key={slug} value={slug}>{_appLabel(slug)}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            {appScope === 'all'
              ? 'Backing up every collection in this database.'
              : `Backing up only ${_appLabel(appScope)} collections${(preview?.gridfs_buckets?.length > 0) ? ' + its GridFS files' : ''}.`}
          </p>
        </div>

        {previewLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : preview ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Database</div>
                <div className="text-sm font-bold text-slate-900 mt-1 truncate" title={preview.db_name}>{preview.db_name}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Collections</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{preview.collections.length}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Documents</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{preview.total_document_count.toLocaleString()}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">GridFS files</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">
                  {preview.gridfs_buckets.reduce((s, b) => s + b.file_count, 0)}
                  <span className="text-xs text-slate-500 font-normal ml-1">({totalGridFsMb.toFixed(1)} MB)</span>
                </div>
              </div>
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900 select-none">
                Show per-collection breakdown
              </summary>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs font-mono text-slate-600">
                {preview.collections.map(c => (
                  <div key={c.name} className="flex justify-between border-b border-slate-100 px-2 py-1">
                    <span>{c.name}</span>
                    <span className="text-slate-400">{c.document_count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              {preview.gridfs_buckets.length > 0 && (
                <>
                  <div className="mt-3 text-xs text-slate-500 font-semibold uppercase tracking-wide">GridFS Buckets</div>
                  <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs font-mono text-slate-600">
                    {preview.gridfs_buckets.map(b => (
                      <div key={b.name} className="flex justify-between border-b border-slate-100 px-2 py-1">
                        <span>{b.name}</span>
                        <span className="text-slate-400">{b.file_count} files · {b.total_size_mb} MB</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </details>

            <Separator />

            <div className="space-y-3">
              <div>
                <Label htmlFor="backup-password" className="font-medium flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  Backup Password (optional — AES-256 encryption)
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="backup-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave blank for an unencrypted backup"
                    autoComplete="new-password"
                    data-testid="backup-password-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowPassword(s => !s)}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                {password && (
                  <div className={`text-xs mt-1 flex items-center gap-1.5 ${password.length >= 12 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {password.length >= 12 ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    {password.length >= 12 ? 'Strong password length' : `Add ${12 - password.length} more characters for a strong password`}
                  </div>
                )}
                {password && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    <b>If you lose this password, the backup CANNOT be recovered.</b> Store it somewhere safe (1Password, BitWarden, etc.) before clicking download.
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3">
                <Switch
                  id="include-gridfs"
                  checked={includeGridFS}
                  onCheckedChange={setIncludeGridFS}
                  data-testid="include-gridfs-toggle"
                />
                <div className="flex-1">
                  <Label htmlFor="include-gridfs" className="font-medium cursor-pointer flex items-center gap-1.5">
                    <HardDrive className="w-3.5 h-3.5" />
                    Include GridFS binaries (resumes, invoices, project files)
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Adds ~{totalGridFsMb.toFixed(1)} MB to the ZIP. Disable for a lean structured-data-only backup.
                  </p>
                </div>
              </div>

              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full md:w-auto"
                size="lg"
                data-testid="download-backup-btn"
              >
                {downloading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating backup… (this can take 30–60s for large DBs)</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Download {password ? 'Encrypted ' : ''}Backup ZIP{appScope !== 'all' ? ` — ${_appLabel(appScope)}` : ''}</>
                )}
              </Button>

              <p className="text-xs text-slate-500">
                💡 <b>Tip:</b> Backups are generated on demand against the <i>currently connected MongoDB instance</i>. If you trigger this on the production deployment, you get production data. If you trigger it on preview, you get preview data.
              </p>
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500 py-6 text-center">Could not load backup preview.</div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────
// Restore tab: upload + inspect + execute with strategy picker
// ─────────────────────────────────────────────────────────────
function RestoreCard({ token }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [strategy, setStrategy] = useState('MERGE');
  const [includeGridFS, setIncludeGridFS] = useState(false);
  const [forceDbMismatch, setForceDbMismatch] = useState(false);
  const [insuranceAck, setInsuranceAck] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f || null);
    setInspection(null);
    setReport(null);
    setInsuranceAck(false);
  };

  const handleInspect = async () => {
    if (!file) return;
    setInspecting(true);
    setInspection(null);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (password) fd.append('password', password);
      const r = await axios.post(`${API}/admin/backup/restore/inspect`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInspection(r.data);
      if (r.data.db_mismatch) {
        toast.warning(`DB name mismatch — backup is from "${r.data.manifest.db_name}", you're on "${r.data.current_db_name}"`);
      } else {
        toast.success('Backup inspected — review and pick a strategy below');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Inspection failed';
      toast.error(msg);
    } finally {
      setInspecting(false);
    }
  };

  const downloadInsuranceBackup = async () => {
    toast.info('Creating insurance backup of current DB...');
    const r = await axios.get(`${API}/admin/backup/download`, {
      params: { include_gridfs: includeGridFS },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      timeout: 300000,
    });
    const blob = new Blob([r.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `INSURANCE-pre-restore-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
    toast.success(`Insurance backup saved (${sizeMb} MB) — keep it safe`);
  };

  const handleExecute = async () => {
    if (!file || !inspection) return;
    if (inspection.db_mismatch && !forceDbMismatch) {
      toast.error('You must acknowledge the DB mismatch before proceeding');
      return;
    }
    if (!insuranceAck) {
      toast.error('Please confirm you understand by checking the insurance backup acknowledgement');
      return;
    }

    // Strong final confirmation
    const confirmMsg =
      strategy === 'REPLACE' ? `⚠️ REPLACE mode\n\nThis will DROP every collection in the backup and re-insert the data. Anything created after the backup will be PERMANENTLY LOST.\n\nProceed?` :
      strategy === 'MERGE'   ? `MERGE mode\n\nDocuments with matching identifiers will be OVERWRITTEN with the backup version. Documents NOT in the backup will be kept.\n\nProceed?` :
                                `ADD_ONLY mode\n\nOnly documents whose identifier doesn't exist yet will be inserted. Existing documents are untouched.\n\nProceed?`;
    if (!window.confirm(confirmMsg)) return;

    setExecuting(true);
    try {
      // STEP 1 — Insurance backup
      await downloadInsuranceBackup();

      // STEP 2 — Execute restore
      toast.info('Executing restore...');
      const fd = new FormData();
      fd.append('file', file);
      if (password) fd.append('password', password);
      fd.append('strategy', strategy);
      fd.append('include_gridfs', includeGridFS);
      fd.append('force_db_mismatch', forceDbMismatch);
      const r = await axios.post(`${API}/admin/backup/restore/execute`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 600000, // 10 min
      });
      setReport(r.data);
      const totalRestored = Object.values(r.data.collections).reduce((s, c) => s + (c.inserted || 0) + (c.updated || 0), 0);
      toast.success(`Restore complete — ${totalRestored.toLocaleString()} documents touched across ${Object.keys(r.data.collections).length} collections`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Restore failed';
      toast.error(msg);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Card data-testid="restore-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Database Restore
        </CardTitle>
        <CardDescription>
          Restore a previously downloaded backup ZIP. Two-step flow: upload &amp; inspect first, then choose a strategy and execute. An insurance backup of the current DB is auto-downloaded before any restore runs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Step 1 — Upload & Inspect */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FileArchive className="w-4 h-4" /> Step 1 — Upload &amp; Inspect
          </div>

          <div>
            <Label htmlFor="restore-file" className="text-xs">Backup ZIP file</Label>
            <Input
              id="restore-file"
              type="file"
              accept=".zip,application/zip"
              onChange={handleFileChange}
              data-testid="restore-file-input"
            />
            {file && <div className="text-xs text-slate-500 mt-1">Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div>}
          </div>

          <div>
            <Label htmlFor="restore-password" className="text-xs flex items-center gap-1">
              <Lock className="w-3 h-3" /> Password (only if the ZIP is encrypted)
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="restore-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank if unencrypted"
                autoComplete="off"
                data-testid="restore-password-input"
              />
              <Button type="button" variant="outline" size="icon" onClick={() => setShowPassword(s => !s)}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Button onClick={handleInspect} disabled={!file || inspecting} data-testid="inspect-backup-btn">
            {inspecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Inspecting…</> : <><FileArchive className="w-4 h-4 mr-2" />Inspect Backup</>}
          </Button>
        </div>

        {/* Step 2 — Review & Execute */}
        {inspection && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4" data-testid="restore-step-2">
            <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Step 2 — Review &amp; Execute
            </div>

            {/* Manifest summary */}
            <div className="bg-white rounded border border-slate-200 p-3 text-sm space-y-1">
              <div><b>Generated:</b> {inspection.manifest.generated_at?.slice(0, 19).replace('T', ' ')}</div>
              <div><b>By:</b> {inspection.manifest.generated_by}</div>
              <div><b>Source DB:</b> {inspection.manifest.db_name}</div>
              <div><b>Format version:</b> {inspection.manifest.format_version}</div>
              <div><b>Encrypted:</b> {inspection.manifest.encrypted ? 'Yes (AES-256)' : 'No'}</div>
              <div><b>Collections in backup:</b> {inspection.collections.length}</div>
              <div><b>GridFS files in backup:</b> {inspection.gridfs.reduce((s, b) => s + b.file_count, 0)}</div>
            </div>

            {/* DB mismatch warning */}
            {inspection.db_mismatch && (
              <div className="bg-red-50 border border-red-300 rounded p-3 text-sm">
                <div className="font-semibold text-red-800 flex items-center gap-1.5 mb-2">
                  <AlertCircle className="w-4 h-4" /> DATABASE MISMATCH
                </div>
                <p className="text-red-700 text-xs mb-2">
                  This backup was generated from <code className="bg-red-100 px-1 rounded">{inspection.manifest.db_name}</code> but you are currently connected to <code className="bg-red-100 px-1 rounded">{inspection.current_db_name}</code>. Restoring across environments (preview ↔ production) is rarely intentional.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={forceDbMismatch} onCheckedChange={setForceDbMismatch} data-testid="force-db-mismatch" />
                  <span className="text-sm text-red-800">I understand and want to proceed across databases</span>
                </label>
              </div>
            )}

            {/* Collection list with backup-vs-current counts */}
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-700 font-medium select-none">
                Show per-collection diff ({inspection.collections.length} collections)
              </summary>
              <div className="mt-2 max-h-60 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr><th className="text-left p-2">Collection</th><th className="text-right p-2">In backup</th><th className="text-right p-2">Currently</th></tr>
                  </thead>
                  <tbody>
                    {inspection.collections.map(c => (
                      <tr key={c.name} className="border-t">
                        <td className="p-2 font-mono">{c.name}</td>
                        <td className="text-right p-2">{c.backup_count}</td>
                        <td className="text-right p-2 text-slate-500">{c.current_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {/* Strategy picker */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Restore strategy</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setStrategy('REPLACE')}
                  className={`text-left p-3 rounded border-2 ${strategy === 'REPLACE' ? 'border-red-500 bg-red-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  data-testid="strategy-REPLACE"
                >
                  <div className="font-semibold text-sm text-red-700">REPLACE</div>
                  <div className="text-xs text-slate-600 mt-1">Drop each collection, re-insert from backup. <b>Existing extras lost.</b></div>
                </button>
                <button
                  type="button"
                  onClick={() => setStrategy('MERGE')}
                  className={`text-left p-3 rounded border-2 ${strategy === 'MERGE' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  data-testid="strategy-MERGE"
                >
                  <div className="font-semibold text-sm text-blue-700">MERGE</div>
                  <div className="text-xs text-slate-600 mt-1">Upsert by ID. <b>Backup overwrites matching docs, extras kept.</b></div>
                </button>
                <button
                  type="button"
                  onClick={() => setStrategy('ADD_ONLY')}
                  className={`text-left p-3 rounded border-2 ${strategy === 'ADD_ONLY' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  data-testid="strategy-ADD_ONLY"
                >
                  <div className="font-semibold text-sm text-emerald-700">ADD-ONLY</div>
                  <div className="text-xs text-slate-600 mt-1">Insert only if ID doesn&apos;t exist. <b>Safest — no existing data touched.</b></div>
                </button>
              </div>
            </div>

            {/* GridFS toggle (only if backup has GridFS) */}
            {inspection.gridfs.length > 0 && inspection.gridfs.some(b => b.file_count > 0) && (
              <div className="flex items-start gap-2">
                <Switch checked={includeGridFS} onCheckedChange={setIncludeGridFS} data-testid="restore-gridfs-toggle" />
                <div className="text-sm">
                  <div className="font-medium">Include GridFS binaries</div>
                  <div className="text-xs text-slate-500">{inspection.gridfs.reduce((s, b) => s + b.file_count, 0)} files in backup ({inspection.gridfs.reduce((s, b) => s + b.total_size_mb, 0).toFixed(1)} MB)</div>
                </div>
              </div>
            )}

            {/* Insurance backup acknowledgement */}
            <div className="bg-blue-50 border border-blue-300 rounded p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={insuranceAck} onCheckedChange={setInsuranceAck} data-testid="insurance-ack" />
                <span className="text-sm text-blue-900">
                  I understand that <b>before any data is touched</b>, an &quot;insurance&quot; backup of my current DB will be auto-downloaded to my computer. I&apos;ll keep it safe in case I need to undo this restore.
                </span>
              </label>
            </div>

            {/* Execute button */}
            <Button
              onClick={handleExecute}
              disabled={executing || !insuranceAck || (inspection.db_mismatch && !forceDbMismatch)}
              className={`w-full ${strategy === 'REPLACE' ? 'bg-red-600 hover:bg-red-700' : ''}`}
              size="lg"
              data-testid="execute-restore-btn"
            >
              {executing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Restoring… (this can take a minute)</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" />Download Insurance Backup &amp; Execute {strategy} Restore</>
              )}
            </Button>
          </div>
        )}

        {/* Restore report */}
        {report && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2" data-testid="restore-report">
            <div className="font-semibold text-emerald-900 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Restore complete
            </div>
            <div className="text-xs text-slate-700">
              Strategy: <b>{report.strategy}</b> · Started {report.started_at?.slice(0, 19).replace('T', ' ')} · Finished {report.finished_at?.slice(0, 19).replace('T', ' ')}
            </div>
            <div className="max-h-60 overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-emerald-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Collection</th>
                    <th className="text-right p-2">In backup</th>
                    <th className="text-right p-2">Inserted</th>
                    <th className="text-right p-2">Updated</th>
                    <th className="text-right p-2">Skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(report.collections).map(([name, stats]) => (
                    <tr key={name} className="border-t">
                      <td className="p-2 font-mono">{name}</td>
                      <td className="text-right p-2">{stats.backup_count}</td>
                      <td className="text-right p-2 text-emerald-700 font-medium">{stats.inserted}</td>
                      <td className="text-right p-2 text-blue-700 font-medium">{stats.updated}</td>
                      <td className="text-right p-2 text-slate-500">{stats.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
